import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import {
  FilledInput,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Switch,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog, { dialogClasses } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Box } from '@mui/system';
import { post } from 'api';
import { url } from 'config/config';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';
import IconifyIcon from 'components/base/IconifyIcon';

const validationSchema = yup.object().shape({
  modeOfPayment: yup
    .string()
    .oneOf(['cheque', 'online', 'cash', 'PDC', 'Subsidy', 'TDS', 'badDebt'], 'Invalid payment Mode')
    .required('Payment mode is required'),

  amount: yup.number().typeError('Amount must be a number').required('Amount is required'),

  // Conditional fields
  chequeNumber: yup.string().when('modeOfPayment', {
    is: (val) => ['cheque', 'PDC'].includes(val),
    then: (schema) => schema.required('cheque number is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  bankName: yup.string().when('modeOfPayment', {
    is: (val) => ['cheque', 'PDC', 'online'].includes(val),
    then: (schema) => schema.required('Bank name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  //  .date()
  //       .typeError('Invalid date')
  //       .max(new Date(), 'Cannot select future dates')
  //       .required('Date Of Joining is required'),
  dateOfReceive: yup
    .date()
    .transform((value, originalValue) => (originalValue ? new Date(originalValue) : null))
    .when(['modeOfPayment', 'received'], {
      is: (mode, received) =>
        ['cash', 'online', 'TDS'].includes(mode) ||
        (['cheque', 'PDC'].includes(mode) && !!received),
      then: (schema) =>
        schema
          .required('Date of receive is required')
          .min(yup.ref('dateOfSubmission'), 'Date of receive cannot be before Date of submission'),
      otherwise: (s) => s.notRequired(),
    })
    .typeError('Invalid date')
    .max(new Date(), 'Cannot select future dates'),
  // dateOfReceive: yup.string().when(['modeOfPayment', 'received'], {
  //   is: (mode, received) =>
  //     ['cash', 'online', 'TDS'].includes(mode) || (['cheque', 'PDC'].includes(mode) && !!received),
  //   then: (s) => s.required('Date of receive is required'),
  //   otherwise: (s) => s.notRequired(),
  // }),
  dateOfCheque: yup
    .date()
    .transform((value, originalValue) => (originalValue ? new Date(originalValue) : null))
    .when('modeOfPayment', {
      is: (val) => ['PDC', 'cheque'].includes(val),
      then: (schema) => schema.required('Date of Cheque is required'),
      otherwise: (schema) => schema.notRequired(),
    })
    .typeError('Invalid date')
    .max(new Date(), 'Cannot select future dates'),

  dateOfSubmission: yup
    .date()
    .transform((value, originalValue) => (originalValue ? new Date(originalValue) : null))
    .when('modeOfPayment', {
      is: (val) => ['PDC', 'cheque'].includes(val),
      then: (schema) =>
        schema
          .required('Date of receive is required')
          .min(yup.ref('dateOfCheque'), 'Date of receive cannot be before Date of submission'),
      otherwise: (schema) => schema.notRequired(),
    })
    .typeError('Invalid date'),
  // .(new Date(), 'Cannot select future dates'),

  transactionId: yup.string().when('modeOfPayment', {
    is: 'online',
    then: (schema) => schema.required('Transaction ID is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  upiId: yup.string().optional(),

  platform: yup.string().when('modeOfPayment', {
    is: 'online',
    then: (schema) => schema.required('Platform is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  remarks: yup.string().optional(),

  // received: yup.boolean().default(false),
  // received: yup.boolean().when('modeOfPayment', {
  //   is: (val) => ['TDS', 'cash', 'online', 'badDebt'].includes(val),
  //   then: (schema) =>
  //     schema.oneOf([true], 'received must be true for this payment mode').required(),
  //   otherwise: (schema) => schema.default(false),
  // }),
  received: yup
    .boolean()
    .default(true)
    .when('bounced', {
      is: true,
      then: (schema) => schema.default(false).oneOf([false], 'received must be false when bounced'),
      otherwise: (schema) =>
        schema.when('modeOfPayment', {
          is: (val) => ['TDS', 'cash', 'online', 'badDebt'].includes(val),
          then: (s) => s.oneOf([true], 'received must be true for this payment mode').required(),
          otherwise: (s) => s.default(true), // default true (as you want)
        }),
    }),

  bounced: yup.boolean().default(false).required(),
  image: yup
    .mixed()
    .nullable()
    .test('fileSize', 'File is too large (max 5MB)', (value) => {
      if (!value) return true;
      if (typeof value === 'string') return true;
      if (value instanceof File) {
        return value.size <= 5 * 1024 * 1024;
      }
      return true;
    }),
});

const ALWAYS_TRUE = ['TDS', 'cash', 'online', 'badDebt'];

// put above the component
const toYMD = (d) => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt?.getTime?.())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// maps incoming payment object (snake_case likely) to form fields
const normalizePaymentToFormDefaults = (p = {}) => ({
  modeOfPayment: p.modeOfPayment || p.mode || '',
  amount: p.amount ?? '',
  bankName: p.bankName ?? p.bank_name ?? '',
  chequeNumber: p.chequeNumber ?? p.cheque_number ?? '',
  dateOfReceive: toYMD(p.dateOfReceive || p.date_of_receive),
  dateOfCheque: toYMD(p.dateOfCheque || p.date_of_cheque),
  dateOfSubmission: toYMD(p.dateOfSubmission || p.date_of_submission),
  transactionId: p.transactionId ?? p.transaction_id ?? '',
  platform: p.platform ?? '',
  upiId: p.upiId ?? p.upi_id ?? '',
  remarks: p.remarks ?? '',
  received: !!(p.received ?? p.recieved), // support both spellings
  bounced: !!p.bounced, // support both spellings
  image: p?.image || null,
});

const AddPayment = ({ open, setOpen, customer, payment, setPayment, fetch }) => {
  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    shouldUnregister: false, // critical for conditional fields
    defaultValues: {
      modeOfPayment: '',
      amount: '',
      bankName: '',
      chequeNumber: '',
      dateOfReceive: '',
      dateOfCheque: '',
      dateOfSubmission: '',
      transactionId: '',
      platform: '',
      upiId: '',
      remarks: '',
      received: false, // keep original spelling as per your payload
      bounced: false, // keep original spelling as per your payload
      image: null,
    },
  });
  const selectedType = watch('modeOfPayment');
  const [preview, setPreview] = useState({
    image: null,
  });
  const [openImage, setOpenImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleFileChange = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      setPreview((prev) => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const isRecieved = watch('received');
  useEffect(() => {
    if (!open || !payment) return;
    // || (Array.isArray(payment) && payment.length === 0)

    const m = normalizePaymentToFormDefaults(payment);

    const keysToPush = [
      'modeOfPayment',
      'chequeNumber',
      'amount',
      'bankName',
      'transactionId',
      'platform',
      'upiId',
      'dateOfCheque',
      'dateOfSubmission',
      'dateOfReceive',
      'remarks',
      'received',
      'bounced',
      'image',
    ];

    keysToPush.forEach((k) => {
      if (m[k] !== undefined && m[k] !== null) {
        setValue(k, m[k], { shouldDirty: false, shouldValidate: false });
      }
    });

    if (m.image) {
      setPreview((prev) => ({
        ...prev,
        image: `${url}${m.image}`, // string URL
      }));
    }
  }, [open, payment, selectedType, setValue]);

  useEffect(() => {
    if (ALWAYS_TRUE.includes(selectedType)) {
      setValue('received', true, { shouldDirty: true, shouldValidate: true });
    }
  }, [selectedType, setValue]);

  const onClose = () => {
    reset();
    setPayment(null);
    setOpen(false);
    setPreview({ image: null });
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const url = payment ? '/users/update_payment' : '/users/create_payment';
      const payload = payment
        ? {
            id: payment?._id,
            customerId: payment?.customerId?._id || payment?.customerId,
            ...data,
          }
        : {
            customerId: customer?._id,
            ...data,
          };
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value instanceof FileList) {
          if (value.length > 0) {
            formData.append(key, value[0]); // only take the first file
          }
        } else if (value instanceof Date) {
          formData.append(key, value.toISOString());
        } else {
          formData.append(key, value);
        }
      });

      const response = await post(url, formData);

      if (response.status == 200) {
        fetch();
        enqueueSnackbar(response.message || 'customer payment add successfully', {
          variant: 'success',
        });
        reset();
        setPayment([]);
        setOpen(false);
      } else {
        reset();
        setPayment([]);
        setOpen(false);

        enqueueSnackbar(response.message || 'Error while adding payment', { variant: 'error' });
      }
    } catch (error) {
      console.log(error);
      reset();
      setPayment([]);
      setOpen(false);

      enqueueSnackbar(error.message || 'Error while adding payment', { variant: 'error' });
    } finally {
      setLoading(false);
      reset();
      setPayment([]);
      setOpen(false);
      setPreview({ image: null });
    }
  };
  const R = watch('received');
  useEffect(() => {
    if (!R) setValue('dateOfReceive', '');
  }, [R]);

  const chequeDate = watch('dateOfCheque');
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="dialog-title"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          [`& .${dialogClasses.paper}`]: {
            p: 0,
            borderRadius: 6,
            width: 1,
            maxWidth: 600,
          },
        }}
      >
        <Stack sx={{ p: 3, pb: 2, justifyContent: 'space-between', position: 'sticky', top: 0 }}>
          <DialogTitle id="dialog-title" sx={{ p: 0, typography: 'h6' }}>
            {payment ? 'Edit Payment' : 'Add Payment'}
          </DialogTitle>
          <Button
            variant="text"
            size="small"
            color="neutral"
            onClick={onClose}
            sx={{ minWidth: 0, p: 0.5 }}
          >
            <IconifyIcon icon="material-symbols:close-rounded" sx={{ fontSize: 20 }} />
          </Button>
        </Stack>

        <DialogContent sx={{ px: 3, py: 1 }}>
          <Grid container spacing={2}>
            {/* Mode of Payment */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="modeOfPayment"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.modeOfPayment}>
                    <InputLabel>Mode Of Payment</InputLabel>
                    <Select {...field} label="Mode Of Payment">
                      {['cheque', 'online', 'cash', 'PDC', 'TDS', 'badDebt'].map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.modeOfPayment?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Amount */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <FormControl variant="filled" fullWidth error={!!errors.amount}>
                    <InputLabel htmlFor="deal-amount">Amount</InputLabel>
                    <FilledInput
                      {...field}
                      id="deal-amount"
                      type="number"
                      inputProps={{ min: 0, step: 'any' }}
                      startAdornment={
                        <InputAdornment position="start">
                          <CurrencyRupeeOutlinedIcon
                            sx={{ fontSize: 18, color: 'text.secondary' }}
                          />
                        </InputAdornment>
                      }
                    />
                    <FormHelperText>{errors.amount?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Bank Name */}
            {['cheque', 'PDC', 'online'].includes(selectedType) && (
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="bankName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Bank Name"
                      variant="filled"
                      fullWidth
                      error={!!errors.bankName}
                      helperText={errors.bankName?.message}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Cheque Number */}
            {['cheque', 'PDC'].includes(selectedType) && (
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="chequeNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cheque Number"
                      variant="filled"
                      fullWidth
                      error={!!errors.chequeNumber}
                      helperText={errors.chequeNumber?.message}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Online-only fields */}
            {selectedType === 'online' && (
              <>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="transactionId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Transaction ID"
                        variant="filled"
                        fullWidth
                        error={!!errors.transactionId}
                        helperText={errors.transactionId?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="platform"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Platform"
                        variant="filled"
                        fullWidth
                        error={!!errors.platform}
                        helperText={errors.platform?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="upiId"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="UPI ID (Optional)" variant="filled" fullWidth />
                    )}
                  />
                </Grid>
              </>
            )}

            {/* Cheque/PDC dates */}
            {['cheque', 'PDC'].includes(selectedType) && (
              <>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="dateOfCheque"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Date Of Cheque"
                        type="date"
                        variant="filled"
                        fullWidth
                        // InputLabelProps={{ shrink: true }}
                        error={!!errors.dateOfCheque}
                        helperText={errors.dateOfCheque?.message}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                          htmlInput: {
                            max: moment().format('YYYY-MM-DD'), // 👉 disables future dates in calendar
                            // onInvalid: (e) => e.preventDefault(), // ❗ blocks browser tooltip
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="dateOfSubmission"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Date Of Submission"
                        type="date"
                        variant="filled"
                        fullWidth
                        // InputLabelProps={{ shrink: true }}
                        error={!!errors.dateOfSubmission}
                        helperText={errors.dateOfSubmission?.message}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                          htmlInput: {
                            min: moment(chequeDate).format('YYYY-MM-DD'), // 👉 disables future dates in calendar
                            // max: moment().format('YYYY-MM-DD'), // 👉 disables future dates in calendar
                            onInvalid: (e) => e.preventDefault(), // ❗ blocks browser tooltip
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {/* Date of Receive (required for ALWAYS_TRUE) */}

            {/* Remarks */}
            <Grid item size={{ xs: 12 }}>
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Remarks"
                    variant="filled"
                    multiline
                    rows={3}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="image"
                control={control}
                render={({ field }) => (
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ textTransform: 'none' }}
                    >
                      {preview?.image?.name || 'Upload Image'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          handleFileChange('image', e);
                        }}
                      />
                    </Button>

                    <Typography color="error" variant="caption">
                      {errors.picture?.message}
                    </Typography>
                  </>
                )}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 3 }}>
              {' '}
              {preview?.image && (
                <Box>
                  <img
                    src={
                      preview.image instanceof File
                        ? URL.createObjectURL(preview.image)
                        : preview.image
                    } // backend URL}
                    alt="Profile"
                    width="50%"
                    style={{ borderRadius: 8, cursor: 'pointer' }}
                    onClick={() => setOpenImage(true)}
                  />
                </Box>
              )}
            </Grid>

            {/* received Switch */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="received"
                control={control}
                render={({ field }) => {
                  const isAlwaysTrue = ALWAYS_TRUE.includes(selectedType);
                  return (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isAlwaysTrue ? true : !!field.value}
                          onChange={(e) => field.onChange(isAlwaysTrue ? true : e.target.checked)}
                          disabled={isAlwaysTrue}
                        />
                      }
                      label="Received"
                    />
                  );
                }}
              />
              {!!errors.received && (
                <FormHelperText error>{errors.received.message}</FormHelperText>
              )}
            </Grid>
            {(ALWAYS_TRUE.includes(selectedType) ||
              (['cheque', 'PDC'].includes(selectedType) && isRecieved)) && (
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="dateOfReceive"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Date Of Receive"
                      type="date"
                      variant="filled"
                      fullWidth
                      // InputLabelProps={{ shrink: true }}
                      error={!!errors.dateOfReceive}
                      helperText={errors.dateOfReceive?.message}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                        htmlInput: {
                          max: moment().format('YYYY-MM-DD'), // 👉 disables future dates in calendar
                          onInvalid: (e) => e.preventDefault(), // ❗ blocks browser tooltip
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item size={{ xs: 12, sm: 6 }}>
              {!['cheque', 'PDC'].includes(selectedType) || (
                <Controller
                  name="bounced"
                  control={control}
                  render={({ field }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            disabled={R}
                          />
                        }
                        label="Cheque Bounced"
                      />
                    );
                  }}
                />
              )}
            </Grid>
            {!!errors.bounced && <FormHelperText error>{errors.bounced.message}</FormHelperText>}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button variant="soft" color="neutral" loading={loading} onClick={() => reset()}>
            Reset
          </Button>
          <Button type="submit" variant="contained" autoFocus loading={loading}>
            {payment ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openImage} onClose={() => setOpenImage(false)} maxWidth="lg">
        <DialogContent sx={{ p: 0 }}>
          {preview?.image && (
            <img
              src={
                preview.image instanceof File ? URL.createObjectURL(preview.image) : preview.image
              }
              alt="Full Preview"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddPayment;
